import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { motion } from 'framer-motion';
import {
  ArrowRight, Users, QrCode, TrendingUp, Sparkles, Smartphone,
  BarChart3, Bell, ShoppingBag, Crown, Zap, Shield, Play, Star,
  CheckCircle2, Package, Calculator, Settings, Award, Ticket,
  LayoutDashboard, Utensils, MapPin, Target, Coffee, Heart, Store, X
} from 'lucide-react';
import SiteNav from '../../components/site/SiteNav';
import SiteFooter from '../../components/site/SiteFooter';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const LandingPage = () => {
  const { user } = useAuth();

  const features = [
    { title: "QR Menü Yönetimi", desc: "AI destekli dijital menü. Ürünlerinizi kategorilerle yönetin, fiyatları anında güncelleyin, müşterileriniz telefondan sipariş versin.", icon: QrCode, color: "#f59e0b" },
    { title: "Akıllı Sadakat Sistemi", desc: "Puan çarpanları, seviye ödülleri ve ziyaret rolleriyle müşterilerinizi ödüllendirin. Uygulama indirmeye gerek yok.", icon: Sparkles, color: "#8b5cf6" },
    { title: "Müşteri Portalı", desc: "Müşterileriniz tarayıcıdan puanlarını görsün, ödüllerini kullansın, kuponlarını kaydetsin. QR ile check-in yapsın.", icon: Smartphone, color: "#ec4899" },
    { title: "Sipariş Yönetimi", desc: "Portal üzerinden gelen siparişleri anlık takip edin. Stok ve muhasebe modülleriyle entegre çalışır.", icon: ShoppingBag, color: "#0ea5e9" },
    { title: "Kampanya & Kuponlar", desc: "İndirim kampanyaları, ödül programları ve kupon sistemiyle satışlarınızı artırın. Hedefli bildirimler gönderin.", icon: Ticket, color: "#f43f5e" },
    { title: "Detaylı Analitik", desc: "Müşteri davranışları, satış trendleri ve ziyaret analizleri. Veriye dayalı kararlar alın.", icon: BarChart3, color: "#10b981" },
    { title: "Stok & Muhasebe", desc: "Envanter takibi ve gelir-gider yönetimi tek panelden. Günlük, haftalık ve aylık raporlar.", icon: Calculator, color: "#6366f1" },
    { title: "Bildirim Sistemi", desc: "Push bildirim ve duyurularla müşterilerinizi kampanyalardan anında haberdar edin.", icon: Bell, color: "#f97316" },
    { title: "Ödeme Entegrasyonları", desc: "iyzico ve diğer ödeme altyapılarıyla güvenli online ödeme. Portal üzerinden tahsilat.", icon: Shield, color: "#059669" },
  ];

  const dashboardFeatures = [
    { icon: LayoutDashboard, label: "Gerçek Zamanlı Metrikler", desc: "Ciro, müşteri ve sipariş takibi" },
    { icon: ShoppingBag, label: "Gelişmiş Sipariş Ekranı", desc: "Ekrana anında düşen siparişler" },
    { icon: Sparkles, label: "Sadakat & Kampanya", desc: "Hedef kitlenize özel promosyonlar" },
    { icon: BarChart3, label: "AI Destekli Raporlar", desc: "Gelecek trendlerini öngören analizler" },
  ];

  const portalFeatures = [
    { icon: QrCode, label: "Sürtünmesiz Check-in", desc: "Tablet veya numara girmeye son" },
    { icon: Utensils, label: "Temassız QR Menü", desc: "Görselli, açıklamalı dijital deneyim" },
    { icon: Star, label: "Puan & Ödül Cüzdanı", desc: "Otomatik artan seviye sistemi" },
    { icon: Bell, label: "Akıllı Bildirimler", desc: "Kişiye özel SMS/Push duyurular" },
  ];

  const stats = [
    { value: "Sıfır", label: "Donanım Maliyeti" },
    { value: "Anında", label: "Kurulum Süresi" },
    { value: "%100", label: "Sürtünmesiz" },
    { value: "AI", label: "Destekli Sistem" },
  ];

  const whyUgra = [
    { title: "Her İşletme İçin Kolay Kullanım", desc: "Kafelerden restoranlara kadar hiçbir eğitim gerektirmeyen, anında kurulup o gün kullanılmaya başlanabilen pratik bir yönetim altyapısı sunuyoruz.", icon: Store },
    { title: "Uygulamasız Müşteri Ağı", desc: "Müşterilerinizi yeni bir uygulama indirmeye zorlamadan çok daha geniş kitlelere ulaşın. Sadece puan toplayarak saniyeler içinde size sadık kalsınlar.", icon: Smartphone },
    { title: "Verilerinizle Akıllı Büyüme", desc: "Satış trendlerini ve yoğunluk saatlerini analiz eden asistanınız sayesinde süreçlerinizi iyileştirin ve gelirinizi veri odaklı kararlarla güvenle büyütün.", icon: Sparkles },
  ];

  const comparisonData = [
    { label: "Kullanım Şartı", ugra: "Anında QR ile (App yok)", other: "Uygulama İndirme Şartı" },
    { label: "Müşteri Kaydı", ugra: "10 Saniyede Profil", other: "Uzun Formlar & SMS Onayı" },
    { label: "İşletme Donanımı", ugra: "Sıfır Donanım (Sadece QR)", other: "Pahalı POS & Kiosk Cihazları" },
    { label: "Veri Analizi", ugra: "Yapay Zeka Destekli Real-time", other: "Manuel Excel Takibi" },
    { label: "Maliyet", ugra: "Şeffaf & Esnek Abonelik", other: "Yüksek Komisyon & Kurulum" },
  ];

  return (
    <div style={{ background: 'var(--bg-main)', color: 'var(--text-main)', minHeight: '100vh', overflowX: 'hidden' }}>
      <SiteNav />

      <style>{`
        .text-gradient { background: linear-gradient(135deg, var(--primary) 0%, #d97706 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
        .gradient-sphere { position: absolute; width: 600px; height: 600px; border-radius: 50%; background: radial-gradient(circle, var(--primary) 0%, transparent 70%); filter: blur(80px); z-index: 0; pointer-events: none; }
        .site-badge { display: inline-flex; align-items: center; gap: 8px; background: rgba(var(--primary-rgb), 0.1); color: var(--primary); padding: 10px 20px; border-radius: 99px; font-size: 0.8125rem; font-weight: 800; text-transform: uppercase; letter-spacing: 1.5px; margin-bottom: 2rem; }
        .feature-card { transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1); cursor: default; }
        .feature-card:hover { transform: translateY(-8px); border-color: var(--primary); box-shadow: 0 30px 60px -15px rgba(var(--primary-rgb), 0.15); }
        .glow-btn { position: relative; overflow: hidden; transition: all 0.3s ease; }
        .glow-btn::after { content: ''; position: absolute; top: -50%; left: -50%; width: 200%; height: 200%; background: linear-gradient(45deg, transparent, rgba(255,255,255,0.3), transparent); transform: rotate(45deg); animation: shimmer 3s infinite; }
        .glow-btn:hover { transform: translateY(-3px); box-shadow: 0 20px 40px -10px rgba(var(--primary-rgb), 0.4); }
        
        .panel-showcase { background: var(--bg-card); border: 1px solid var(--border); border-radius: 24px; padding: 2.5rem; transition: all 0.3s ease; position: relative; overflow: hidden; }
        .panel-showcase:hover { border-color: var(--primary); transform: translateY(-5px); box-shadow: 0 25px 50px -12px rgba(var(--primary-rgb), 0.1); }
        .showcase-icon-wrapper { width: 48px; height: 48px; border-radius: 14px; display: flex; align-items: center; justify-content: center; margin-bottom: 1.5rem; }
        
        @keyframes shimmer { 0% { transform: translateX(-150%) rotate(45deg); } 100% { transform: translateX(150%) rotate(45deg); } }
        
        @media (max-width: 768px) {
          .hero-section { padding: 8rem 5% 4rem !important; }
          .hero-title { font-size: 2.5rem !important; }
          .hero-buttons { flex-direction: column !important; }
          .hero-buttons a { width: 100%; justify-content: center; box-sizing: border-box; }
          .section-pad { padding: 4rem 5% !important; }
          .feature-grid { grid-template-columns: 1fr !important; }
          .panels-grid { grid-template-columns: 1fr !important; }
          .stats-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 0.75rem !important; }
          .why-grid { grid-template-columns: 1fr !important; }
          .plans-preview { flex-direction: column !important; }
        }
        @media (max-width: 1024px) and (min-width: 769px) {
          .feature-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>

      {/* Hero */}
      <section className="hero-section" style={{ padding: '14rem 10% 6rem', position: 'relative', textAlign: 'center' }}>
        <div className="gradient-sphere" style={{ top: '-10%', left: '50%', transform: 'translateX(-50%)', opacity: 0.12 }} />
        <motion.div 
          initial="hidden" animate="visible" variants={staggerContainer}
          style={{ position: 'relative', zIndex: 1 }}
        >
          <motion.div variants={fadeUp} className="site-badge">
            <Zap size={16} /> Yeni Nesil İşletme Yönetimi
          </motion.div>
          <motion.h1 variants={fadeUp} className="hero-title" style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)', fontWeight: 900, lineHeight: 1.05, letterSpacing: '-0.04em', marginBottom: '1.5rem' }}>
            Müşterini <span className="text-gradient">Tanı</span>,<br />İşletmeni <span className="text-gradient">Büyüt</span>.
          </motion.h1>
          <motion.p variants={fadeUp} style={{ fontSize: '1.2rem', color: 'var(--text-muted)', maxWidth: '680px', margin: '0 auto 2.5rem', lineHeight: 1.7 }}>
            Uygulama indirmeye veya pahalı donanımlara gerek yok. Sürtünmesiz sadakat, akıllı QR menü ve AI destekli CRM ile tanışın.
          </motion.p>
          <motion.div variants={fadeUp} className="hero-buttons" style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to={user ? "/app" : "/signup"} className="glow-btn" style={{ padding: '1rem 2.5rem', borderRadius: '16px', fontSize: '1rem', background: 'var(--primary)', color: 'white', fontWeight: 800, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '10px' }}>
              {user ? 'Panele Git' : 'Ücretsiz Başla'} <ArrowRight size={20} />
            </Link>
            <Link to="/pricing" style={{ padding: '1rem 2.5rem', borderRadius: '16px', fontSize: '1rem', border: '1px solid var(--border)', color: 'var(--text-main)', fontWeight: 700, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' }}>
              Planları İncele
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* Stats */}
      <section className="section-pad" style={{ padding: '0 10% 6rem' }}>
        <motion.div 
          initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={staggerContainer}
          className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem', maxWidth: '900px', margin: '0 auto' }}
        >
          {stats.map((s, i) => (
            <motion.div variants={fadeUp} key={i} className="card glass" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '20px', padding: '2rem', textAlign: 'center' }}>
              <div style={{ fontSize: '2.5rem', fontWeight: 900, color: 'var(--primary)', marginBottom: '0.25rem' }}>{s.value}</div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', fontWeight: 600 }}>{s.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Neden Ugra? */}
      <section id="why" className="section-pad" style={{ padding: '6rem 10%', background: 'rgba(var(--primary-rgb), 0.02)' }}>
        <motion.div 
          initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}
          style={{ maxWidth: '1200px', margin: '0 auto' }}
        >
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <motion.div variants={fadeUp} className="site-badge" style={{ marginBottom: '1rem' }}><Target size={14} /> Neden ugra?</motion.div>
            <motion.h2 variants={fadeUp} style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)', fontWeight: 900, letterSpacing: '-0.03em', marginBottom: '1rem' }}>Geleneksel Yöntemleri Unutun</motion.h2>
            <motion.p variants={fadeUp} style={{ color: 'var(--text-muted)', fontSize: '1.1rem', maxWidth: '600px', margin: '0 auto' }}>Esnafın dijitalleşme sürecini kolaylaştırıyor, karmaşık kuralları ortadan kaldırıyoruz.</motion.p>
          </div>

          <div className="why-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '2rem' }}>
            {whyUgra.map((item, i) => (
              <motion.div variants={fadeUp} key={i} style={{ padding: '2rem', background: 'var(--bg-main)', borderRadius: '24px', border: '1px solid var(--border)' }}>
                <div style={{ width: 56, height: 56, borderRadius: '16px', background: 'rgba(var(--primary-rgb), 0.1)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem' }}>
                  <item.icon size={28} />
                </div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 800, marginBottom: '0.75rem' }}>{item.title}</h3>
                <p style={{ color: 'var(--text-muted)', lineHeight: 1.6 }}>{item.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Integrated Comparison Table */}
          <div style={{ marginTop: '5rem' }}>
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <motion.h3 variants={fadeUp} style={{ fontSize: '1.75rem', fontWeight: 900, marginBottom: '0.5rem' }}>ugra Farkı</motion.h3>
              <motion.p variants={fadeUp} style={{ color: 'var(--text-muted)' }}>Neden modern işletmeler ugra'yı tercih ediyor?</motion.p>
            </div>

            <motion.div variants={fadeUp} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '24px', overflow: 'hidden', maxWidth: '1000px', margin: '0 auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', background: 'rgba(var(--primary-rgb), 0.05)', padding: '1.25rem 2rem', borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontWeight: 800, color: 'var(--text-muted)', fontSize: '0.85rem', textTransform: 'uppercase' }}>ÖZELLİK</div>
                <div style={{ fontWeight: 900, color: 'var(--primary)', fontSize: '0.85rem' }}>ugra</div>
                <div style={{ fontWeight: 800, color: 'var(--text-muted)', fontSize: '0.85rem' }}>DİĞERLERİ</div>
              </div>
              {comparisonData.map((item, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', padding: '1.25rem 2rem', borderBottom: i === comparisonData.length - 1 ? 'none' : '1px solid var(--border)', transition: 'background 0.2s' }} className="comp-row">
                  <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{item.label}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-main)', fontWeight: 600, fontSize: '0.9rem' }}>
                    <CheckCircle2 size={16} style={{ color: '#10b981' }} /> {item.ugra}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                    <X size={16} style={{ color: '#ef4444' }} /> {item.other}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Showcase: Business & Customer Panels */}
      <section className="section-pad" style={{ padding: '8rem 10%' }}>
        <motion.div 
          initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}
          style={{ maxWidth: '1200px', margin: '0 auto' }}
        >
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <motion.div variants={fadeUp} className="site-badge" style={{ marginBottom: '1rem' }}><CheckCircle2 size={14} /> Kusursuz Uyum</motion.div>
            <motion.h2 variants={fadeUp} style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)', fontWeight: 900, letterSpacing: '-0.03em', marginBottom: '1rem' }}>Çift Taraflı Deneyim</motion.h2>
            <motion.p variants={fadeUp} style={{ color: 'var(--text-muted)', fontSize: '1.1rem', maxWidth: '600px', margin: '0 auto' }}>Hem işletmeniz hem de müşterileriniz için özel tasarlanmış modern arayüzler.</motion.p>
          </div>

          <div className="panels-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
            {/* Business Panel Showcase */}
            <motion.div variants={fadeUp} className="panel-showcase">
              <div style={{ position: 'absolute', top: 0, right: 0, width: '150px', height: '150px', background: 'var(--primary)', filter: 'blur(100px)', opacity: 0.15, borderRadius: '50%' }} />
              <div className="showcase-icon-wrapper" style={{ background: 'rgba(var(--primary-rgb), 0.1)', color: 'var(--primary)' }}>
                <LayoutDashboard size={24} />
              </div>
              <h3 style={{ fontSize: '1.75rem', fontWeight: 900, marginBottom: '1rem' }}>Yeni Nesil İşletme Paneli</h3>
              <p style={{ color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: '2rem' }}>
                İşletmenizi tek ekrandan yönetin. Siparişleri onaylayın, menünüzü anında güncelleyin ve AI raporlarıyla kararlar alın. Tüm verileriniz güvende ve her an elinizin altında.
              </p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                {dashboardFeatures.map((f, i) => (
                  <div key={i} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'rgba(var(--primary-rgb), 0.1)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>
                      <f.icon size={12} />
                    </div>
                    <div>
                      <h4 style={{ fontWeight: 800, fontSize: '0.95rem', marginBottom: '0.2rem' }}>{f.label}</h4>
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: '2.5rem' }}>
                <a 
                  href="https://panel.ugra.io" 
                  target="_blank" 
                  rel="noreferrer"
                  className="glow-btn"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'var(--primary)', color: 'white', padding: '12px 24px', borderRadius: '12px', fontWeight: 800, textDecoration: 'none', fontSize: '0.95rem' }}
                >
                  Panele Giriş Yap <ArrowRight size={18} />
                </a>
              </div>
            </motion.div>

            {/* Customer Portal Showcase */}
            <motion.div variants={fadeUp} className="panel-showcase">
              <div style={{ position: 'absolute', top: 0, right: 0, width: '150px', height: '150px', background: '#ec4899', filter: 'blur(100px)', opacity: 0.15, borderRadius: '50%' }} />
              <div className="showcase-icon-wrapper" style={{ background: 'rgba(236,72,153,0.1)', color: '#ec4899' }}>
                <Smartphone size={24} />
              </div>
              <h3 style={{ fontSize: '1.75rem', fontWeight: 900, marginBottom: '1rem' }}>Müşteri Portalı (WebApp)</h3>
              <p style={{ color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: '2rem' }}>
                Uygulama indirmeye son. Müşterileriniz QR kodu okutarak saniyeler içinde size özel portala girer, menüyü inceler, sipariş verir ve sadakat puanlarını takip eder.
              </p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                {portalFeatures.map((f, i) => (
                  <div key={i} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'rgba(236,72,153,0.1)', color: '#ec4899', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>
                      <f.icon size={12} />
                    </div>
                    <div>
                      <h4 style={{ fontWeight: 800, fontSize: '0.95rem', marginBottom: '0.2rem' }}>{f.label}</h4>
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: '2.5rem' }}>
                <a 
                  href="https://app.ugra.io" 
                  target="_blank" 
                  rel="noreferrer"
                  className="glow-btn"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#ec4899', color: 'white', padding: '12px 24px', borderRadius: '12px', fontWeight: 800, textDecoration: 'none', fontSize: '0.95rem' }}
                >
                  Portalı Deneyimle <Smartphone size={18} />
                </a>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Features Grid */}
      <section id="features" className="section-pad" style={{ padding: '4rem 10% 8rem' }}>
        <motion.div 
          initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}
        >
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <motion.div variants={fadeUp} className="site-badge" style={{ marginBottom: '1rem' }}><Sparkles size={14} /> Özellikler</motion.div>
            <motion.h2 variants={fadeUp} style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)', fontWeight: 900, letterSpacing: '-0.03em', marginBottom: '1rem' }}>Tek Platform, Çok İşlev</motion.h2>
            <motion.p variants={fadeUp} style={{ color: 'var(--text-muted)', fontSize: '1.1rem', maxWidth: '600px', margin: '0 auto' }}>İşletmenizi bir üst seviyeye taşıyacak tüm araçlar tek pakette.</motion.p>
          </div>
          <div className="feature-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem', maxWidth: '1200px', margin: '0 auto' }}>
            {features.map((f, i) => (
              <motion.div variants={fadeUp} key={i} className="card glass feature-card" style={{ padding: '2rem' }}>
                <div style={{ background: `${f.color}15`, color: f.color, width: 48, height: 48, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem' }}>
                  <f.icon size={24} />
                </div>
                <h3 style={{ fontSize: '1.125rem', fontWeight: 800, marginBottom: '0.75rem' }}>{f.title}</h3>
                <p style={{ color: 'var(--text-muted)', lineHeight: 1.7, fontSize: '0.9rem' }}>{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Plans */}
      <section className="section-pad" style={{ padding: '6rem 10%', background: 'rgba(var(--primary-rgb), 0.02)' }}>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <motion.h2 variants={fadeUp} style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)', fontWeight: 900, letterSpacing: '-0.03em', marginBottom: '1rem' }}>Her İşletmeye Uygun Plan</motion.h2>
            <motion.p variants={fadeUp} style={{ color: 'var(--text-muted)', fontSize: '1.1rem' }}>Ücretsiz başlayın, büyüdükçe yükseltin.</motion.p>
          </div>
          <div className="plans-preview" style={{ display: 'flex', gap: '1.5rem', justifyContent: 'center', flexWrap: 'wrap', maxWidth: '900px', margin: '0 auto' }}>
            {[
              { name: 'Başlangıç', price: '0 ₺', icon: <Zap size={20} />, desc: 'Basit QR Menü ve Standart Portal', color: 'var(--text-muted)', border: 'var(--border)' },
              { name: 'Pro', price: '990 ₺/ay', icon: <Star size={20} />, desc: 'Tam CRM, Sınırsız Sadakat, Panel', color: 'var(--primary)', border: 'var(--primary)', popular: true },
              { name: 'Premium', price: '1.990 ₺/ay', icon: <Crown size={20} />, desc: 'AI Danışman + Entegrasyonlar', color: '#c026d3', border: '#c026d3' },
            ].map((p, i) => (
              <motion.div variants={fadeUp} key={i} style={{ flex: '1 1 260px', maxWidth: 300, background: 'var(--bg-card)', border: `2px solid ${p.popular ? p.border : 'var(--border)'}`, borderRadius: 24, padding: '2.5rem 2rem', textAlign: 'center', position: 'relative' }}>
                {p.popular && <div style={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)', background: 'var(--primary)', color: 'white', padding: '6px 16px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 800, whiteSpace: 'nowrap' }}>EN POPÜLER</div>}
                <div style={{ color: p.color, marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}>{p.icon}</div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 800, marginBottom: '0.5rem' }}>{p.name}</h3>
                <div style={{ fontSize: '2.25rem', fontWeight: 900, marginBottom: '0.5rem', letterSpacing: '-1px' }}>{p.price}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '1.5rem', lineHeight: 1.5 }}>{p.desc}</div>
                <Link to="/pricing" style={{ display: 'block', padding: '12px', borderRadius: 14, background: p.popular ? 'var(--primary)' : 'var(--bg-main)', color: p.popular ? 'white' : 'var(--text-main)', fontWeight: 800, textDecoration: 'none', fontSize: '0.9rem', border: p.popular ? 'none' : '1px solid var(--border)', transition: 'all 0.2s' }}>Planı Seç</Link>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Hakkımızda Snippet */}
      <section className="section-pad" style={{ padding: '6rem 10%' }}>
        <motion.div 
          initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}
          style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}
        >
          <motion.div variants={fadeUp} style={{ display: 'inline-flex', padding: '16px', borderRadius: '50%', background: 'rgba(var(--primary-rgb), 0.05)', color: 'var(--primary)', marginBottom: '1.5rem' }}>
            <Coffee size={32} />
          </motion.div>
          <motion.h2 variants={fadeUp} style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: 900, letterSpacing: '-0.02em', marginBottom: '1.5rem' }}>Hakkımızda</motion.h2>
          <motion.p variants={fadeUp} style={{ fontSize: '1.15rem', lineHeight: 1.8, color: 'var(--text-muted)', marginBottom: '2rem' }}>
            ugra, yerel işletmelerin dijital dönüşümünü basit ve erişilebilir kılan bir <strong style={{ color: 'var(--text-main)' }}>fraxlabs</strong> ürünüdür. Amacımız, işletmeler ile müşterileri arasındaki bağı güçlü, modern yazılım çözümleriyle güçlendirmektir. Geleceğin sadakat ve operasyon vizyonunu İstanbul'dan itibaren inşa ediyoruz.
          </motion.p>
          <motion.div variants={fadeUp}>
            <Link to="/about" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: 'var(--primary)', fontWeight: 800, textDecoration: 'none', fontSize: '1.05rem' }}>
              Tüm Detaylar <ArrowRight size={18} />
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* CTA */}
      <section className="section-pad" style={{ padding: '0 10% 8rem', textAlign: 'center' }}>
        <motion.div 
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          style={{ padding: '5rem 3rem', background: 'var(--primary)', color: 'white', borderRadius: 32, maxWidth: 900, margin: '0 auto', position: 'relative', overflow: 'hidden' }}
        >
          <div style={{ position: 'absolute', top: '-50%', left: '-50%', width: '200%', height: '200%', background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 60%)', filter: 'blur(30px)' }} />
          <h2 style={{ position: 'relative', zIndex: 1, fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 900, marginBottom: '1rem', letterSpacing: '-0.02em' }}>Geleceğin İşletmesi Olun</h2>
          <p style={{ position: 'relative', zIndex: 1, fontSize: '1.15rem', opacity: 0.9, marginBottom: '2.5rem', maxWidth: 500, margin: '0 auto 2.5rem' }}>
            ugra ile müşteri bağlarınızı güçlendirin ve operasyonel yükten kurtulun.
          </p>
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
            <Link to="/signup" style={{ background: 'white', color: 'var(--primary)', padding: '1rem 2.5rem', borderRadius: 16, fontWeight: 800, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8, transition: 'transform 0.2s' }}>
              Hemen Başla <Play size={16} fill="var(--primary)" />
            </Link>
            <Link to="/contact" style={{ border: '1px solid rgba(255,255,255,0.3)', color: 'white', padding: '1rem 2.5rem', borderRadius: 16, fontWeight: 700, textDecoration: 'none', transition: 'background 0.2s' }}>
              İletişime Geç
            </Link>
          </div>
        </motion.div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default LandingPage;
